import React, { useState } from 'react';
import { Shield, Mail, Lock, User, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { auth, getLegalDocUrl } from '../utils/api';

/** Purpose: Executes { on login, on forgot password }. Inputs: { onLogin, onForgotPassword }. Output: Operation result. */
export default function AuthPage({ onLogin, onForgotPassword }) {
  const privacyDocUrl = getLegalDocUrl('privacy');
  const termsDocUrl = getLegalDocUrl('terms');
  const hipaaDocUrl = getLegalDocUrl('hipaa-notice');

  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
  });

  /** Purpose: Handles change. Inputs: e. Output: Operation result. */
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  /** Purpose: Handles submit. Inputs: e. Output: Promise with operation result. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isLogin) {
        // Login
        const { ok, data } = await auth.login(formData.email, formData.password);

        if (!ok) {
          if (data.error === 'MFA_REQUIRED') {
            setError('MFA not yet supported in UI');
          } else {
            setError(data.message || 'Invalid email or password');
          }
          return;
        }

        onLogin(data);
      } else {
        // Register
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return;
        }

        if (formData.password.length < 12) {
          setError('Password must be at least 12 characters');
          return;
        }

        const result = await auth.register(
          formData.email,
          formData.password,
          formData.firstName,
          formData.lastName
        );

        if (result.error) {
          setError(result.message || 'Registration failed');
          return;
        }

        // Auto-login after registration
        const { ok, data } = await auth.login(formData.email, formData.password);
        if (ok) {
          onLogin(data);
        } else {
          setError('Account created. Please sign in.');
          setIsLogin(true);
        }
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen mesh-gradient flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background orbs */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-brand-400/10 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-violet-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-400/5 rounded-full blur-3xl" />

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center animated-gradient p-4 rounded-2xl shadow-elevated mb-5">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-brand-600 via-violet-600 to-brand-500 bg-clip-text text-transparent tracking-tight">
            MedCaddy
          </h1>
          <p className="text-slate-500 mt-1.5 text-sm">Secure Medical Records Management</p>
        </div>

        {/* Form Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-elevated border border-white/60 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-1 tracking-tight">
            {isLogin ? 'Welcome back' : 'Create account'}
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            {isLogin ? 'Sign in to access your records' : 'Get started with MedCaddy'}
          </p>

          {error && (
            <div className="mb-5 p-4 bg-red-50/80 border border-red-200/60 rounded-2xl flex items-start gap-3 animate-fade-in">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all"
                      placeholder="John"
                      required={!isLogin}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all"
                    placeholder="Doe"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all"
                  placeholder="••••••••••••"
                  required
                />
              </div>
              {!isLogin && (
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Min 12 chars, uppercase, lowercase, number, special character
                </p>
              )}
            </div>

            {!isLogin && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all"
                    placeholder="••••••••••••"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}
            {onForgotPassword && isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-brand-600 via-violet-600 to-brand-500 text-white font-semibold rounded-xl hover:shadow-glow-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 group"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                <>
                  {isLogin ? 'Sign in' : 'Create account'}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

          <p className="mt-5 text-[11px] text-slate-400 leading-relaxed text-center">
            By continuing, you agree to the{' '}
            <a href={termsDocUrl} className="text-brand-600 hover:text-brand-700 font-medium">Terms</a>
            {', '}
            <a href={privacyDocUrl} className="text-brand-600 hover:text-brand-700 font-medium">Privacy</a>
            {' & '}
            <a href={hipaaDocUrl} className="text-brand-600 hover:text-brand-700 font-medium">HIPAA Notice</a>.
          </p>

          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <p className="text-sm text-slate-500">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="text-brand-600 font-semibold hover:text-brand-700"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-6">
          <p className="text-[11px] text-slate-400">HIPAA Compliant</p>
          <span className="text-slate-300">|</span>
          <a href="/legal" className="text-[11px] text-brand-500 hover:text-brand-600 font-medium">Legal</a>
        </div>
      </div>
    </div>
  );
}
